/**
 * Mercenary Mission System Configuration
 *
 * Missions are automated dungeon/encounter runs by mercenary squads.
 * - Squads have a success rate based on power vs dungeon difficulty
 * - Failure can result in mercenary death (permadeath)
 * - Rewards are reduced compared to manual runs (70%)
 */

/**
 * Mission configuration
 */
export const MISSION_CONFIG = {
  // Base mission duration (5 minutes)
  baseDuration: 300000,

  // Duration multipliers by dungeon tier
  tierDurationMultipliers: {
    tier1: 1.0,   // 5 min
    tier2: 1.5,   // 7.5 min
    tier3: 2.0,   // 10 min
    tier4: 3.0,   // 15 min
    tier5: 4.0,   // 20 min
  },

  // Success rate bounds
  minSuccessRate: 0.10,   // 10% minimum even with weak squad
  maxSuccessRate: 0.95,   // 95% maximum even with strong squad

  // Death mechanics (on failure only)
  deathChanceOnFailure: 0.30,  // 30% death chance per merc on failed mission

  // Reward multipliers (compared to manual dungeon completion)
  rewardMultiplier: 0.70,      // 70% of normal rewards
  xpMultiplier: 0.50,          // 50% XP for mercenaries
  playerXpMultiplier: 0.30,    // 30% XP for player (passive income)

  // Cooldown after mission (before same squad can go again)
  cooldownAfterMission: 600000, // 10 minutes

  // Partial rewards on failure (what you still get)
  failureRewardMultiplier: 0.30, // 30% rewards on failure
};

/**
 * Recommended squad power per dungeon tier
 * Based on expected mercenary stats at appropriate levels
 */
export const TIER_POWER_REQUIREMENTS = {
  // Tier 1 - Levels 5-20
  tier1_lv5: 100,
  tier1_lv10: 150,
  tier1_lv15: 220,
  tier1_lv20: 300,

  // Tier 2 - Levels 25-40
  tier2_lv25: 400,
  tier2_lv30: 550,
  tier2_lv35: 750,
  tier2_lv40: 1000,

  // Tier 3 - Levels 45-60
  tier3_lv45: 1300,
  tier3_lv50: 1700,
  tier3_lv55: 2200,
  tier3_lv60: 2800,

  // Tier 4 - Levels 65-80
  tier4_lv65: 3500,
  tier4_lv70: 4500,
  tier4_lv75: 5800,
  tier4_lv80: 7500,

  // Tier 5 - Levels 85-100
  tier5_lv85: 9500,
  tier5_lv90: 12000,
  tier5_lv95: 15000,
  tier5_lv100: 20000,
};

/**
 * Get recommended power for a dungeon tier
 * @param {string} tierId - Dungeon tier ID (e.g., 'tier1_lv10')
 * @returns {number} Recommended squad power
 */
export function getRecommendedPower(tierId) {
  return TIER_POWER_REQUIREMENTS[tierId] || 100;
}

/**
 * Calculate mission duration based on tier
 * @param {string} tierId - Dungeon tier ID
 * @returns {number} Duration in milliseconds
 */
export function calculateMissionDuration(tierId) {
  // Extract tier number (tier1, tier2, etc.)
  const tierMatch = tierId.match(/tier(\d)/);
  const tierNum = tierMatch ? parseInt(tierMatch[1]) : 1;
  const tierKey = `tier${tierNum}`;

  const multiplier = MISSION_CONFIG.tierDurationMultipliers[tierKey] || 1.0;
  return Math.floor(MISSION_CONFIG.baseDuration * multiplier);
}

/**
 * Calculate success rate for a mission
 * @param {number} squadPower - Total power of the squad
 * @param {string} tierId - Target dungeon tier
 * @returns {number} Success probability (0.1 - 0.95)
 */
export function calculateMissionSuccessRate(squadPower, tierId) {
  const recommendedPower = getRecommendedPower(tierId);
  const { minSuccessRate, maxSuccessRate } = MISSION_CONFIG;

  // Calculate power ratio
  const ratio = squadPower / recommendedPower;

  // Linear scaling:
  // ratio 0.5 = minSuccessRate (10%)
  // ratio 1.5 = maxSuccessRate (95%)
  const normalizedRatio = (ratio - 0.5) / 1.0;
  const successRate = minSuccessRate + (normalizedRatio * (maxSuccessRate - minSuccessRate));

  // Clamp to bounds
  return Math.max(minSuccessRate, Math.min(maxSuccessRate, successRate));
}

/**
 * Get success rate color for UI display
 * @param {number} successRate - Success probability (0-1)
 * @returns {{ color: string, label: string }}
 */
export function getSuccessRateDisplay(successRate) {
  const percent = Math.round(successRate * 100);

  if (successRate >= 0.80) {
    return { color: '#22c55e', label: 'Very High', percent }; // Green
  }
  if (successRate >= 0.60) {
    return { color: '#84cc16', label: 'High', percent }; // Lime
  }
  if (successRate >= 0.40) {
    return { color: '#eab308', label: 'Moderate', percent }; // Yellow
  }
  if (successRate >= 0.25) {
    return { color: '#f97316', label: 'Low', percent }; // Orange
  }
  return { color: '#ef4444', label: 'Very Low', percent }; // Red
}

/**
 * Mission status types
 */
export const MISSION_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PENDING_COLLECTION: 'pending_collection',
};

/**
 * Format mission duration for display
 * @param {number} ms - Time in milliseconds
 * @returns {string} Formatted time string
 */
export function formatMissionTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Calculate expected rewards for mission preview
 * @param {string} tierId - Dungeon tier ID
 * @param {Object} dungeonConfig - DUNGEON_CONFIG.TIERS entry
 * @returns {{ xp: number, lootChance: string }}
 */
export function getExpectedRewards(tierId, dungeonConfig) {
  if (!dungeonConfig) {
    return { xp: 0, lootChance: '0%' };
  }

  const baseXp = dungeonConfig.expReward || 0;
  const adjustedXp = Math.floor(baseXp * MISSION_CONFIG.rewardMultiplier);

  // Estimate loot chance based on loot table
  const lootTableRates = {
    common: '18%',
    uncommon: '21%',
    rare: '22%',
    epic: '24%',
    legendary: '26%',
    mythic: '27%',
  };
  const lootChance = lootTableRates[dungeonConfig.lootTable] || '20%';

  return { xp: adjustedXp, lootChance };
}
